/**
 * Recognises errors that originate in a mobile in-app browser's own native
 * bridge, NOT in Panda code, so Sentry can drop them before they pollute error
 * analysis or burn quota.
 *
 * Android/iOS in-app browsers (Facebook, Instagram, and other Meta/embedded
 * shells) inject an instrumentation script — served from an `iabjs://`
 * pseudo-URL, e.g. `iabjs://navigation_performance_logger_android` — that
 * beacons navigation/performance data to the native app over a JS→Java bridge.
 * When the page unloads (for us, the Google OAuth redirect during signup) that
 * bridge can throw:
 *
 *   Uncaught Error: Error invoking postMessage: Java exception was raised
 *   during method invocation
 *     at sendDataToNative (iabjs://navigation_performance_logger_android:1:…)
 *     at sendBeforeUnloadMessage (iabjs://navigation_performance_logger_android:1:…)
 *
 * This is entirely inside the browser shell and does not break the user flow —
 * the navigation and signup still complete. It should never be treated as a
 * Panda frontend crash.
 *
 * The same shell throws a differently-worded twin on iOS. There the in-app
 * browser is a `WKWebView`, and its bridge talks to the native app through
 * `window.webkit.messageHandlers.<name>.postMessage(…)` rather than a JS→Java
 * call. On unload — after the native side has torn the handler down — WebKit
 * rejects that call with `InvalidAccessError`, whose engine-standard
 * description is verbatim "The object does not support the operation or
 * argument." (PostHog issue 01a0469c, Facebook Mobile / iOS 26.2.1):
 *
 *   DOMException: InvalidAccessError: The object does not support the
 *   operation or argument.
 *     at postMessage                      ← the shell's bridge, no source URL
 *     at @sentry/browser/src/helpers.ts   ← Sentry's wrap() re-reporting it
 *
 * There is no `iabjs://` marker and no `Java exception` wording to key on, so
 * the Android signals all miss it. It is recognised structurally instead: an
 * `InvalidAccessError` thrown from a NATIVE frame named `postMessage` — WebKit
 * prints native functions as `postMessage@[native code]`, and both stack
 * parsers (Sentry's gecko parser and posthog-js's fork of it) capture that
 * literal `[native code]` as the frame's filename. A frame with a real script
 * URL never matches: that is someone's attributable code, not the bridge.
 *
 * Deliberately NO "keep if a Panda frame is present" veto, unlike
 * cross-origin-frame-noise.ts. This predicate runs at `beforeSend`/`before_send`
 * time, on RAW, unresolved stacks — and the Sentry SDK frame that re-reported
 * the throw (its `browserApiErrors` integration wraps `addEventListener`/timers
 * document-wide, so it wrapped the shell's callback, caught the throw, and
 * rethrew it with a real stack) is bundled into Panda's own
 * `/_next/static/chunks/*.js`. A Panda-frame veto would therefore fire on
 * EVERY instance of this error and neuter the drop; the `node_modules` path
 * only exists in PostHog's UI after server-side sourcemap resolution. (Same
 * raw-frame reality the opaque-script drop already documents for bundled
 * posthog-js frames.)
 *
 * The drop is safe without that veto because Panda's own `postMessage` surface
 * cannot raise this error: every Panda call is `window.postMessage` to a frame
 * (preview iframe, parent), whose failure modes are `SyntaxError` (bad
 * targetOrigin) and `DataCloneError` (unclonable payload). `InvalidAccessError`
 * from a native `postMessage` arises from `webkit.messageHandlers` and detached
 * `MessagePort`s — and the repo uses neither, a fact LOCKED by a sweep test in
 * this module's test file. If that sweep ever fails, this predicate must be
 * revisited before the new API lands.
 *
 * Pure and near-dependency-free (only `isGeneratedAppPreviewError`, itself pure
 * and Sentry-free) so it is trivially unit-testable and reusable on any capture
 * path. It accepts the minimal Sentry event shape it inspects; a real
 * `@sentry/nextjs` `ErrorEvent` satisfies it structurally.
 */

import { isGeneratedAppPreviewError } from "./error-origin";

interface NoiseStackFrame {
  filename?: string;
  abs_path?: string;
  module?: string;
  function?: string;
}

interface NoiseExceptionValue {
  type?: string;
  value?: string;
  stacktrace?: { frames?: NoiseStackFrame[] };
}

export interface InAppBrowserNoiseEvent {
  /** Sentry's `Event.message` (string in practice; typed loose for safety). */
  message?: unknown;
  exception?: { values?: NoiseExceptionValue[] };
}

// The injected-script pseudo-scheme / logger name is the strongest,
// browser-version-independent marker that a frame came from the IAB shell.
const BRIDGE_SOURCE_RE = /iabjs:\/\/|navigation_performance_logger/i;

// Named bridge functions still identify the shell even when a browser strips
// the frame's URL (some IABs report frames with a function name but no file).
const BRIDGE_FUNCTION_RE = /^(?:sendDataToNative|sendBeforeUnloadMessage)$/;

// The bridge's own postMessage→native failure. Both halves are required so a
// genuine in-app postMessage bug of ours is never silenced by accident. Panda
// is a pure web app with no native Java bridge, so "Java exception … method
// invocation" can only come from a WebView/IAB shell.
const BRIDGE_MESSAGE_RE =
  /invoking postMessage[\s\S]*Java exception was raised during method invocation/i;

function frameIsBridge(frame: NoiseStackFrame): boolean {
  const source = frame.filename ?? frame.abs_path ?? "";
  if (BRIDGE_SOURCE_RE.test(source)) return true;
  const fn = frame.function ?? "";
  return BRIDGE_FUNCTION_RE.test(fn);
}

function frameSourceOf(frame: NoiseStackFrame): string {
  return frame.filename ?? frame.abs_path ?? frame.module ?? "";
}

// The WebKit in-app-browser (iOS) arm. The error NAME is the marker — matched on
// the combined `type: value` so both serialisations are covered: posthog-js may
// emit `type: "DOMException"` with the name in the value ("InvalidAccessError:
// …") or `type: "InvalidAccessError"` with the description in the value. The
// generic WebKit description is not FB-specific, so keying on it adds nothing.
const WEBKIT_BRIDGE_ERROR_RE = /\bInvalidAccessError\b/;

// What a stack parser leaves in `filename` for a frame with no attributable
// script URL. WebKit prints `postMessage@[native code]`, and the shared gecko
// regex (Sentry stack-parsers.js; posthog-js exception-autocapture ships the
// identical pattern) captures the literal `[native code]` — verified in both
// packages, and the reason a plain "empty filename" check would miss the real
// production event. Empty/absent stays covered for shells that strip the frame.
const NATIVE_OR_ABSENT_SOURCE_RE = /^(?:\[native code\]|native|<anonymous>)?$/;

// The shell's bridge call is attributed to a NATIVE frame literally named
// `postMessage` — the WebView's own `WKScriptMessageHandler`, not a Panda call
// site (ours name the caller and carry a real script URL). A sourced
// `postMessage` frame does NOT match: that is someone's attributable code.
// `bound postMessage` covers a handler the shell registered via `.bind()`.
function frameIsWebkitBridgePostMessage(frame: NoiseStackFrame): boolean {
  const fn = frame.function ?? "";
  if (fn !== "postMessage" && fn !== "bound postMessage") return false;
  return NATIVE_OR_ABSENT_SOURCE_RE.test(frameSourceOf(frame).trim());
}

/**
 * True when the event is the iOS WebKit in-app-browser bridge teardown — an
 * `InvalidAccessError` thrown from a native `postMessage` frame.
 *
 * A preview/sandbox-owned event is kept regardless, so a real crash in a
 * user's generated app is never swallowed here — it flows on to
 * `classifyErrorOrigin` and files as `generated-app-preview`. There is
 * intentionally no Panda-frame veto: see the module header — on the raw stack
 * this hook actually sees, the bundled error-SDK wrapper frame reads as a
 * `/_next/static/` chunk on every instance, so a veto would disable the drop
 * outright rather than protect anything. Safety comes from the error-name +
 * native-throw-site pairing plus the sweep-test lock on the only APIs able to
 * produce it.
 */
function isWebkitInAppBridgeNoise(event: InAppBrowserNoiseEvent): boolean {
  // A preview/sandbox-owned throw is the generated app's, not the shell's —
  // keep it so classifyErrorOrigin can file it as generated-app-preview.
  if (isGeneratedAppPreviewError(event)) return false;

  let matchedError = false;
  let hasBridgeFrame = false;

  for (const value of event.exception?.values ?? []) {
    const combined = `${value.type ?? ""}: ${value.value ?? ""}`;
    if (WEBKIT_BRIDGE_ERROR_RE.test(combined)) matchedError = true;
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frameIsWebkitBridgePostMessage(frame)) hasBridgeFrame = true;
    }
  }

  return matchedError && hasBridgeFrame;
}

/**
 * True when the event is in-app-browser native-bridge noise that should be
 * dropped from error reporting. Covers both shell arms of the same actor: the
 * Android JS→Java bridge (`iabjs://` / "Java exception …") and the iOS WebKit
 * bridge (`InvalidAccessError` from a source-less `postMessage` frame).
 */
export function isInAppBrowserBridgeNoise(
  event: InAppBrowserNoiseEvent | null | undefined,
): boolean {
  if (!event) return false;

  for (const value of event.exception?.values ?? []) {
    const combined = `${value.type ?? ""}: ${value.value ?? ""}`;
    if (BRIDGE_MESSAGE_RE.test(combined)) return true;
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frameIsBridge(frame)) return true;
    }
  }

  if (isWebkitInAppBridgeNoise(event)) return true;

  // Fallback for events carrying only a top-level message (no exception values).
  const message = typeof event.message === "string" ? event.message : "";
  return BRIDGE_MESSAGE_RE.test(message);
}
